import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Modal } from '../../../components/common/Modal';
import { useLanguage } from '../../../features/language/useLanguage';
import type { Member, Role } from '../../../services/memberService';
import { useMemberLabels } from './memberLabels';

export interface InviteError {
  title: string;
  message: string;
  existingMember?: Member;
}

interface InviteErrorModalProps {
  error: InviteError | null;
  roles: Role[];
  onClose: () => void;
  onReactivate: (member: Member) => void;
}

/** Error modal shown when an invite fails, offering reactivation for inactive duplicates. */
export const InviteErrorModal: React.FC<InviteErrorModalProps> = ({ error, roles, onClose, onReactivate }) => {
  const { t } = useLanguage();
  const { roleLabel, statusLabel } = useMemberLabels(roles);
  const existing = error?.existingMember;

  return (
    <Modal isOpen={!!error} onClose={onClose} title={error?.title ?? ''}>
      <div className="space-y-4" data-test-id="member-invite-error-modal">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
          </div>
          <p className="text-sm text-gray-600">{error?.message}</p>
        </div>
        {existing && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm">
            <div className="font-medium text-gray-900">{existing.full_name}</div>
            <div className="text-gray-500">{existing.email}</div>
            <div className="text-xs text-gray-500 mt-1">
              {roleLabel(existing.role)} ·{' '}
              {existing.is_active
                ? statusLabel(existing.status)
                : t('settingsMember.status.inactive', undefined, 'Inactive')}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            data-test-id="member-invite-error-close-button"
            className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            onClick={onClose}
          >
            {t('auth.common.close', undefined, 'Close')}
          </button>
          {existing && !existing.is_active && (
            <button
              type="button"
              data-test-id="member-invite-error-reactivate-button"
              className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              onClick={() => onReactivate(existing)}
            >
              {t('settingsMember.actions.reactivateMember', undefined, 'Reactivate')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};
